import { useSelectedLocation } from 'contexts';
import { useCallback, useMemo } from 'react';
import { ActiveUnit } from 'types';
import { useNewTransaction } from '.';

const useValidate = () => {
  const { chassis, container, genset, stack, transaction } = useNewTransaction();
  const { selectedLocation } = useSelectedLocation();

  const bookingIds: number[] = useMemo(() => {
    const bookingIdSet = [chassis, container, genset, ...stack]
      .map((unit?: ActiveUnit | null) => unit?.booking?.id)
      .reduce((acc, id) => (!id ? acc : acc.add(id)), new Set<number>());
    return [...bookingIdSet.values()];
  }, [chassis, container, genset, stack]);

  const customerIds: number[] = useMemo(() => {
    const customerIdSet = [chassis, container, genset, ...stack]
      .map((unit?: ActiveUnit | null) => unit?.customer?.id)
      .reduce((acc, id) => (!id ? acc : acc.add(id)), new Set<number>());
    return [...customerIdSet.values()];
  }, [chassis, container, genset, stack]);

  const inbound: boolean | undefined = useMemo(() => transaction.inbound, [transaction.inbound]);

  const locationId: number | undefined = useMemo(() => selectedLocation?.id, [selectedLocation]);

  const redeliveryIds: number[] = useMemo(() => {
    const redeliveryIdSet = [chassis, container, genset, ...stack]
      .map((unit?: ActiveUnit | null) => unit?.redelivery?.id)
      .reduce((acc, id) => (!id ? acc : acc.add(id)), new Set<number>());
    return [...redeliveryIdSet.values()];
  }, [chassis, container, genset, stack]);

  const jsonHasMessage = useCallback((json?: unknown): json is { message: string } => {
    return !!json && typeof json === 'object' && 'message' in json && typeof json.message === 'string';
  }, []);

  const validateTruckingCompany = useCallback(
    async (truckingCompanyId: number) => {
      try {
        const response = await fetch(
          new URL(`/api/trucking-companies/${truckingCompanyId}/validation`, window.location.origin),
          {
            method: 'POST',
            headers: new Headers({
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }),
            body: JSON.stringify({ bookingIds, customerIds, inbound, locationId, redeliveryIds })
          }
        );
        if (response.status === 400) {
          const json = await response.json();
          if (jsonHasMessage(json)) {
            return json.message.replace(/400 BAD_REQUEST "([^"]+)"/, '$1');
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    [bookingIds, customerIds, inbound, locationId, redeliveryIds, jsonHasMessage]
  );

  return { validateTruckingCompany };
};

export default useValidate;
